import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { config } from '@platform/constants';
import { httpService } from '@shared/utilities';
import { AscResponse } from '@shared/models';
import * as Response from '@shared/models/showcase/showcase.response.type';
import * as Request from '@shared/models/showcase/showcase.request.type';
import { formatErrorMessage } from '@ascendnow/ascend-components';
import { setCertificate, setCertificateList } from '../slices/certificate.slice';
import { setBadgesByCategory, setLatestBadges ,setBadgesByCategoryId} from '../slices/badge.slice';
import { setCompetitionsWins } from '../slices/competition-wins.slice';



export const ShowcaseEndpoints = {
    getCertificateList: config.base_url + '/showcase/certificates',
    getCertificate: (certificateId: string) => config.base_url + `/showcase/certificates/${certificateId}`,
    getBadgesByCategoryId: (categoryId: string,type?:'skill' | 'entrepreneurship' | 'internship') => config.base_url + `/showcase/badges/category/${categoryId}/earned?type=${type}`,
    getLatestBadges: config.base_url + '/showcase/badges',
    getBadgesByCategory: config.base_url + '/showcase/badges/category',
    getCompetitionWins: config.base_url + '/showcase/competition',
    updateCertificate: (certificateId: string) => config.base_url + `/showcase/certificate/${certificateId}/name`,
    shareCertificate: (certificateId: string) => config.base_url + `/showcase/certificates/${certificateId}/update`,
};

export const fetchCertificateList = createAsyncThunk<void, Request.IFetchCertificateListQueryType>(
    ShowcaseEndpoints.getCertificateList,
    async (query, { dispatch }) => {
        try {
            const queryParams = query.params ? new URLSearchParams({ ...query.params }) : '';
            const res = await httpService.get<AscResponse<Response.IFetchCertificateListResponseType>>(
                `${ShowcaseEndpoints.getCertificateList}?${queryParams}`
            );

            dispatch(setCertificateList(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching list of certifcate, query: ${ShowcaseEndpoints.getCertificateList}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchSingleCertificate = createAsyncThunk<void, Request.IFetchCertificateQueryType>(
    ShowcaseEndpoints.getBadgesByCategoryId('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IFetchCertificateResponseType>>(
                ShowcaseEndpoints.getCertificate(query.params.certificateId)
            );

            dispatch(setCertificate(res.data.data['certificate']));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching single certificate, query: ${ShowcaseEndpoints.getCertificate('id')}`
                ),
                { type: 'error' }
            );
        } finally {
            if (query.cb && query.cb.onFinally) {
                query.cb.onFinally();
            }
        }
    }
);

export const fetchLatestBadges = createAsyncThunk<void, Request.IFetchLatestBadgesQueryType>(
    ShowcaseEndpoints.getLatestBadges,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IFetchLatestBadgesResponseType>>(
                `${ShowcaseEndpoints.getLatestBadges}`
            );

            dispatch(setLatestBadges(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(err, `Error fetching latest badges, query: ${ShowcaseEndpoints.getLatestBadges}`),
                { type: 'error' }
            );
        }
    }
);
export const fetchBatchesByCategoryId = createAsyncThunk<void, Request.IFetchBadgeCategoryQueryIdType>(
    ShowcaseEndpoints.getBadgesByCategoryId('categoryId'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IBadgePerCategory[]>>(
                ShowcaseEndpoints.getBadgesByCategoryId(query.params.categoryId,query.params.type)
            );
            dispatch(setBadgesByCategoryId(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching single badges, query: ${ShowcaseEndpoints.getBadgesByCategoryId('categoryId')}`
                ),
                { type: 'error' }
            );
        } finally {
            if (query.cb && query.cb.onFinally) {
                query.cb.onFinally();
            }
        }
    }
);
export const fetchBadgesByCategory = createAsyncThunk<void, Request.IFetchBadgesByCategoryQueryType>(
    ShowcaseEndpoints.getBadgesByCategory,
    async (query, { dispatch }) => {
        try {
            const queryParams = query.params ? new URLSearchParams({ ...query.params }) : '';
            const res = await httpService.get<AscResponse<Response.IFetchBadgesByCategoryResponseType>>(
                `${ShowcaseEndpoints.getBadgesByCategory}?${queryParams}`
            );

            dispatch(setBadgesByCategory(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching badges by category, query: ${ShowcaseEndpoints.getBadgesByCategory}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchCompetitionsWins = createAsyncThunk<void, Request.IFetchCompetitionsWinsQueryType>(
    ShowcaseEndpoints.getCompetitionWins,
    async (query, { dispatch }) => {
        try {
            const queryParams = query.params ? new URLSearchParams({ ...query.params }) : '';
            const res = await httpService.get<AscResponse<Response.IFetchCompetitionsWinsResponseType>>(
                `${ShowcaseEndpoints.getCompetitionWins}?${queryParams}`
            );

            dispatch(setCompetitionsWins(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching competitions wins, query: ${ShowcaseEndpoints.getCompetitionWins}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const updateCertificateName = createAsyncThunk<void, Request.IUpdateCertificateNameQueryType>(
    ShowcaseEndpoints.updateCertificate('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.patch<AscResponse<Response.IFetchCertificateResponseType>>(
                ShowcaseEndpoints.updateCertificate(query.params.certificateId),
                { name: query.params.name }
            );

            dispatch(setCertificate(res.data.data));

            toast.success('Your certificate has been successfully updated');

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error updating the name of certificate owner, query: ${ShowcaseEndpoints.updateCertificate('id')}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const shareCertificate = createAsyncThunk<void, Request.IShareCertificateQueryType>(
    ShowcaseEndpoints.shareCertificate('id'),
    async (query, { dispatch }) => {
        try {
            const formData = new FormData();
            formData.append('image', query.params.image);
            const res = await httpService.patch<AscResponse<Response.IFetchCertificateResponseType>>(
                ShowcaseEndpoints.shareCertificate(query.params.certificateId),
                formData
            );

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error on sharing a certificate, query: ${ShowcaseEndpoints.shareCertificate('id')}`
                ),
                { type: 'error' }
            );
        } finally {
            if (query.cb && query.cb.onFinally) {
                query.cb.onFinally();
            }
        }
    }
);
